import React, { useEffect, useState } from "react";
import { Col, Row, Space, Spin, Table, Typography, Tooltip, Modal, Button, Empty } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { Application } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { notification } from "antd";
import { Link } from "react-router-dom";
import { PrimaryButton } from "../shared/button/Buttons";

type ApplicationColumns = ColumnsType<Application>;

const Applications: React.FC = () => {
  const { apiKey } = usePermissions();
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);

  const navigate = useNavigate();

  const styles = {
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    detailsHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
  };

  // Add minimal CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .applications-container {
        background-color: #f5f7fa;
      }
      .interactive-row {
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const deleteApplication = (applicationId: number) => {
    Modal.confirm({
      title: "Delete Application",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to delete this application? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        setIsLoading(true);
        ApplicationService.deleteApplication(
          applicationId,
          setIsLoading,
          apiKey
        ).subscribe(
          () => {
            setApplications((prevApplications) =>
              prevApplications.filter((app) => app.id !== applicationId)
            );
            setTotalCount(prev => prev - 1);
            setIsLoading(false);
            notification.success({
              message: "Success",
              description: "Application deleted successfully",
            });
          },
          (error: any) => {
            console.error(error);
            setIsLoading(false);
            notification.error({
              message: "Error",
              description: "Failed to delete application",
            });
          }
        );
      },
    });
  };

  const applicationColumns: ApplicationColumns = [
    {
      title: "Name",
      dataIndex: "application_name",
      key: "application_name",
      width: "30%",
      render: (text: string) => (
        <Typography.Text style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Typography.Text>
      ),
      sorter: (a, b) => a.application_name.localeCompare(b.application_name),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "35%",
      render: (text: number) => {
        const date = moment(text);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) => moment(a.date_created).unix() - moment(b.date_created).unix(),
    },
    {
      title: "Last Activity",
      dataIndex: "last_activity",
      key: "last_activity",
      width: "35%",
      render: (timestamp: number) => {
        if (timestamp > 0) {
          const date = moment(timestamp);
          return (
            <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
              <div>
                <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
                <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
              </div>
            </Tooltip>
          );
        } else {
          return <Typography.Text type="secondary">N/A</Typography.Text>;
        }
      },
      sorter: (a, b) => {
        const dateA = a.last_activity > 0 ? a.last_activity : 0;
        const dateB = b.last_activity > 0 ? b.last_activity : 0;
        return dateB - dateA;
      },
    },
    {
      title: "",
      key: "action",
      width: "48px",
      render: (_, record) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          danger
          onClick={(e) => {
            e.stopPropagation();
            deleteApplication(record.id);
          }}
        />
      ),
      align: "right",
    },
  ];

  useEffect(() => {
    refreshApplications();
  }, [apiKey]);

  const refreshApplications = () => {
    ApplicationService.getApplications(setIsLoading, apiKey).subscribe(
      (apps: Application[]) => {
        // Sort applications by last_activity in descending order
        const sortedApps = apps.sort((a, b) => {
          const dateA = a.last_activity > 0 ? a.last_activity : 0;
          const dateB = b.last_activity > 0 ? b.last_activity : 0;
          return dateB - dateA;
        });
        setApplications(sortedApps);
        setTotalCount(sortedApps.length);
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  const paginatedApplications = applications.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (isLoading && !applications.length) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 200px)" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto", minHeight: "calc(100vh - 64px)" }}>
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <Row align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/">Home</Link> / Applications
              </Typography.Text>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 600 }}>
                Applications
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Manage your applications and their settings
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <div style={styles.contentCard}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Applications
          </Typography.Title>
        </div>
        <div style={styles.tableContainer}>
          <Table
            rowKey={(record) => record.id.toString()}
            columns={applicationColumns}
            dataSource={paginatedApplications}
            loading={isLoading}
            rowClassName={() => "interactive-row"}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalCount,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} items`,
            }}
            onChange={handleTableChange}
            onRow={(record) => {
              return {
                onClick: () => navigate(`/application/${record.id}`),
              };
            }}
            locale={{
              emptyText: (
                <Empty 
                  description={
                    <div>
                      <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
                        No applications found
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        Create a new application to get started.
                      </Typography.Text>
                    </div>
                  } 
                  image={Empty.PRESENTED_IMAGE_SIMPLE} 
                />
              )
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Applications;
